import { useState } from 'react';

import { Row } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { LoaderIcon, TrashIcon } from 'lucide-react';
import {
  Credenza,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { EventRule } from '@/features/event-rules/schemas/event-rules.schema';
import { useDeleteEventRulesMutation } from '@/features/event-rules/hooks/mutations/use-delete-event-rules.ts';

export function EventRulesTableToolbarDeleteDialog({
  eventRules,
  onSuccess,
  showTrigger = true,
}: {
  eventRules: Row<EventRule>['original'][];
  onSuccess?: () => void;
  showTrigger?: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutate: deleteEventRuleMutation, isPending: isDeletePending } =
    useDeleteEventRulesMutation({
      onSuccess: () => {
        onSuccess?.();
        setIsDialogOpen(false);
      },
    });

  const onClickDelete = () => {
    deleteEventRuleMutation({
      eventRuleIds: eventRules.map((n) => n.eventRuleId),
    });
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      {showTrigger ? (
        <CredenzaTrigger asChild>
          <Button variant="outline" size="sm" className="flex w-min gap-2">
            <TrashIcon className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline-block">Delete ({eventRules.length})</span>
          </Button>
        </CredenzaTrigger>
      ) : null}
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>Confirm Event Rules Deletion</CredenzaTitle>
          <CredenzaDescription>
            Are you sure you want to delete{' '}
            <span className="font-medium">
              {eventRules.map((n) => n.eventTitle).join(', ')}
            </span>
            {eventRules.length === 1 ? ' event rule' : ' event rules'}? This action cannot be
            undone.
          </CredenzaDescription>
        </CredenzaHeader>
        <CredenzaFooter className="gap-2 sm:space-x-0">
          <CredenzaClose asChild>
            <Button variant="outline">Cancel</Button>
          </CredenzaClose>
          <Button
            aria-label="Delete selected rows"
            variant="destructive"
            onClick={onClickDelete}
            disabled={isDeletePending}
          >
            {isDeletePending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Delete
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
}

export default EventRulesTableToolbarDeleteDialog;
